/**
 * How a primitive's CONTENT flows: one set of props, one mapping, for every primitive that holds
 * text (30.10.2026, Pilot #226: a caller may place a primitive, never style it).
 *
 * Alignment, clamping, wrapping, figures and the focus treatment used to arrive as classes from
 * pages (`text-right tabular-nums` on 20 table cells, `line-clamp-2` on list titles, `focus-ring`
 * on focusable cards). Under the placement allowlist (`placement-rules.json`) those are appearance,
 * so they are props here, mapped to the same classes, and each primitive applies them just before
 * the caller's placement classes, exactly where the page's classes used to sit.
 */
import { cn } from './utils'

export type ContentAlign = 'left' | 'center' | 'right'

export interface ContentProps {
  /** Text alignment. `{ md: 'right' }` aligns from the md breakpoint up. */
  align?: ContentAlign | { md: ContentAlign }
  /** One line with an ellipsis, or at most two lines. */
  clamp?: 1 | 2
  /** `nowrap` keeps one line, `normal` lets a label wrap, `words` and `all` break long words or anywhere. */
  wrap?: 'nowrap' | 'normal' | 'words' | 'all'
  /** Figures of equal width, for amounts and counts that line up. */
  numeric?: boolean
  /** `ring`: the shared focus ring on a focusable surface. `none`: a programmatic focus target (tabIndex -1) that must not read as a selection. */
  focus?: 'ring' | 'none'
}

// Literal class strings, so Tailwind sees every one of them.
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const
const ALIGN_MD = { left: 'md:text-left', center: 'md:text-center', right: 'md:text-right' } as const
const CLAMP = { 1: 'truncate', 2: 'line-clamp-2' } as const
const WRAP = { nowrap: 'whitespace-nowrap', normal: 'whitespace-normal', words: 'break-words', all: 'break-all' } as const
const FOCUS = { ring: 'focus-ring', none: 'focus:outline-none' } as const

export function contentClasses({ align, clamp, wrap, numeric, focus }: ContentProps): string {
  return cn(
    align && (typeof align === 'string' ? ALIGN[align] : ALIGN_MD[align.md]),
    clamp && CLAMP[clamp],
    wrap && WRAP[wrap],
    numeric && 'tabular-nums',
    focus && FOCUS[focus],
  )
}

/** Takes the content props out of a primitive's rest props, so they never reach the DOM. */
export function splitContent<T extends ContentProps>({ align, clamp, wrap, numeric, focus, ...rest }: T): [ContentProps, Omit<T, keyof ContentProps>] {
  return [{ align, clamp, wrap, numeric, focus }, rest]
}
